// Transfers tab of a place (CR_Manager_vs_User_role §5.2 / §5.3; "Movements" until CR_20260915_Request_Qty R0 — the
// URL key stays ?tab=movements). One list (owner, 2026-09-15): the transfers into and out of its sites in a period
// (open ones first) and, among them by date, the movements no transfer explains (lib/movementRows). One date per row:
// a transfer's latest step. On a project, managers also see what each transfer does to billing; everyone else sees
// neutral wording (P7). The other end of a transfer is named through transfer_places(), so a project user sees the
// warehouse's name. New transfer lives in the page header, labelled "Transfers".
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { HistoryEvent } from '@fleetspoke/core/types';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { useSession } from '../../auth/session';
import { Empty, Loading, LoadError } from '../Page';
import { useProfileNames } from '../../data/billing';
import { usePlaceHistory } from '../../data/queries';
import {
  OPEN_TRANSFER_STATUSES,
  type ProjectMovement,
  TRANSFER_STATUS_COLOR,
  usePlaceMovements,
} from '../../data/workflow';
import {
  type BillingEffect,
  billingEffect,
  isoDateIn,
  type MovementDirection,
  movementDirection,
  movementsInPeriod,
  type PeriodKind,
  periodInstants,
  periodRange,
} from '../../lib/billing';
import { latestStep, type TransferStep, unexplainedEvents } from '../../lib/movementRows';
import { dateTime } from '../../lib/time';
import { usePlaceNames } from '../NewTransferDialog';

export interface MovementView {
  m: ProjectMovement;
  direction: MovementDirection;
  effect: BillingEffect;
}

type PlaceHistoryEvent = HistoryEvent & { asset_id: string; assets: { asset_code: string } | null };

/** A row of the Transfers tab: a transfer, or a movement no transfer explains. */
export type MovementRow =
  | { kind: 'transfer'; key: string; at: string; step: TransferStep | null; view: MovementView }
  | { kind: 'event'; key: string; at: string; event: PlaceHistoryEvent };

const EFFECT_COLOR: Record<BillingEffect, string> = {
  starts_on_receipt: 'warning.main',
  started: 'success.main',
  billed_until_receipt: 'info.main',
  stopped: 'text.secondary',
  partial: 'error.main',
  none: 'text.secondary',
};

const SHOWN_CODES = 5;
// lines that did not go the way the transfer did — worth naming next to the asset
export const ODD_LINE = ['not_received', 'disputed', 'superseded'];
// location events fetched per period; when the fetch is full the list says it shows only the latest
const EVENTS_FETCH = 200;

/** Pending lines of open transfers in one direction: assets on their way that receipt has not settled yet. */
const pendingAssets = (views: MovementView[], direction: MovementDirection) =>
  views
    .filter((v) => v.direction === direction && OPEN_TRANSFER_STATUSES.includes(v.m.status))
    .reduce((n, v) => n + v.m.transfer_lines.filter((l) => l.status === 'pending').length, 0);

/**
 * The period's transfers of a set of sites (with direction and billing effect) and the list rows: open transfers
 * first, then closed transfers and unexplained movements together, newest first. Plus the names they show.
 */
export function useMovementViews(siteIds: string[], fromIso: string, toIso: string) {
  const { t } = useTranslation();
  const movements = usePlaceMovements(siteIds, toIso);
  const history = usePlaceHistory(siteIds, fromIso, toIso, EVENTS_FETCH);
  const views = useMemo<MovementView[]>(() => {
    if (!fromIso || !movements.data) return [];
    const mine = new Set(siteIds);
    return movementsInPeriod(movements.data, fromIso, toIso).map((m) => {
      const direction = movementDirection(m, mine);
      return { m, direction, effect: billingEffect(m, direction) };
    });
  }, [movements.data, fromIso, toIso, siteIds]);
  const rows = useMemo<MovementRow[]>(() => {
    const transferRow = (view: MovementView): MovementRow => ({
      kind: 'transfer',
      key: `t${view.m.id}`,
      view,
      ...latestStep(view.m),
    });
    const open = views.filter((v) => OPEN_TRANSFER_STATUSES.includes(v.m.status));
    const rest: MovementRow[] = [
      ...views.filter((v) => !open.includes(v)).map(transferRow),
      ...unexplainedEvents(
        history.data ?? [],
        views.map((v) => v.m),
      ).map((event): MovementRow => ({ kind: 'event', key: `h${event.id}`, at: event.at, event })),
    ];
    return [...open.map(transferRow), ...rest.sort((a, b) => b.at.localeCompare(a.at))];
  }, [views, history.data]);
  const names = useProfileNames(views.map((v) => v.m.received_by ?? '').filter(Boolean));
  const siteName = usePlaceNames();
  const receiver = (m: ProjectMovement) =>
    m.receipt_method === 'gateway_auto'
      ? t('movements.by_gateway')
      : (m.received_by && names.data?.get(m.received_by)) || '';
  return {
    views,
    rows,
    capped: history.data?.length === EVENTS_FETCH,
    loading: movements.isLoading || history.isLoading,
    error: movements.error ?? history.error,
    siteName,
    receiver,
  };
}

export function MovementsTable({
  views,
  rows,
  capped,
  loading,
  error,
  siteName,
  receiver,
  showBilling,
  chips,
}: {
  views: MovementView[];
  rows: MovementRow[];
  capped: boolean;
  loading: boolean;
  error: unknown;
  siteName(id: string | null, side?: 'from' | 'to'): string;
  receiver(m: ProjectMovement): string;
  /** a project's managers: the billing column, and "not billed yet / still billed" wording */
  showBilling: boolean;
  /** more chips for the summary line (a project's open requests) */
  chips?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const at = (iso: string | null) => dateTime(iso, i18n.language, tz) ?? '—';
  const pendingIn = pendingAssets(views, 'in');
  const pendingOut = pendingAssets(views, 'out');
  const caption = (text: ReactNode) => (
    <Typography variant="caption" color="text.secondary" display="block">
      {text}
    </Typography>
  );
  const assetLink = (id: string, code: string | undefined) => (
    <RouterLink to={`/assets/${id}`}>{code ?? '—'}</RouterLink>
  );

  const transferCells = ({
    view: { m, direction, effect },
    at: when,
    step,
  }: Extract<MovementRow, { kind: 'transfer' }>) => (
    <>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {at(when)}
        {step
          ? caption(
              step === 'received' && receiver(m)
                ? `${t(`movements.${step}`)} · ${receiver(m)}`
                : t(`movements.${step}`),
            )
          : null}
      </TableCell>
      <TableCell>
        <RouterLink to={`/transfers/${m.id}`}>{m.ref}</RouterLink>
        {m.created_by_system ? (
          <Chip size="small" sx={{ ml: 1 }} label={t('transfers.automatic')} />
        ) : null}
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
            label={t(`movements.dir_${direction}`)}
          />
          {siteName(m.from_site_id, 'from')} → {siteName(m.to_site_id, 'to')}
        </Typography>
      </TableCell>
      <TableCell>
        {m.transfer_lines.slice(0, SHOWN_CODES).map((l, i) => (
          <span key={l.asset_id}>
            {i ? ', ' : ''}
            {assetLink(l.asset_id, l.assets?.asset_code)}
            {ODD_LINE.includes(l.status) ? ` (${t(`transfers.line_${l.status}`)})` : ''}
          </span>
        ))}
        {m.transfer_lines.length > SHOWN_CODES
          ? caption(t('movements.more', { count: m.transfer_lines.length - SHOWN_CODES }))
          : null}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={TRANSFER_STATUS_COLOR[m.status]}
          label={t(`m.transfer.status.${m.status}`)}
        />
      </TableCell>
      {showBilling ? (
        <TableCell>
          <Typography variant="body2" sx={{ color: EFFECT_COLOR[effect] }}>
            {t(`movements.effect_${effect}`)}
          </Typography>
        </TableCell>
      ) : null}
    </>
  );

  const eventCells = ({ event: e }: Extract<MovementRow, { kind: 'event' }>) => (
    <>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{at(e.at)}</TableCell>
      <TableCell>
        {t(`event.${e.event}`, { site: e.sites?.name ?? '', vehicle: e.vehicle_ref ?? '' })}
        {e.source ? caption(t(`source.${e.source}`)) : null}
      </TableCell>
      <TableCell>{assetLink(e.asset_id, e.assets?.asset_code)}</TableCell>
      <TableCell>{caption(t('movements.no_transfer'))}</TableCell>
      {showBilling ? <TableCell>—</TableCell> : null}
    </>
  );

  return (
    <Box data-testid="movements">
      {pendingIn || pendingOut || chips ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {pendingIn ? (
            <Chip
              color="warning"
              variant="outlined"
              label={t(showBilling ? 'movements.pending_in' : 'places.pending_in', {
                count: pendingIn,
              })}
            />
          ) : null}
          {pendingOut ? (
            <Chip
              color="info"
              variant="outlined"
              label={t(showBilling ? 'movements.pending_out' : 'places.pending_out', {
                count: pendingOut,
              })}
            />
          ) : null}
          {chips}
        </Stack>
      ) : null}
      {loading ? <Loading /> : null}
      {error ? <LoadError error={error} /> : null}
      {!loading && !error && !rows.length ? <Empty>{t('movements.none')}</Empty> : null}
      {rows.length ? (
        <TableContainer>
          <Table size="small" data-testid="movements-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('movements.col_date')}</TableCell>
                <TableCell>{t('movements.col_movement')}</TableCell>
                <TableCell>{t('transfers.assets')}</TableCell>
                <TableCell>{t('transfers.status')}</TableCell>
                {showBilling ? <TableCell>{t('movements.billing')}</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.key}
                  hover
                  data-testid={
                    r.kind === 'transfer' ? `movement-${r.view.m.ref}` : 'movement-event'
                  }
                >
                  {r.kind === 'transfer' ? transferCells(r) : eventCells(r)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
      {capped ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('movements.other_capped', { count: EVENTS_FETCH })}
        </Typography>
      ) : null}
    </Box>
  );
}

type ShortPeriod = Exclude<PeriodKind, 'to_date'>;

/** This month · Last month · Custom, as calendar dates in the company timezone. */
export function PeriodPicker({
  kind,
  onKind,
  custom,
  onCustom,
  range,
  kinds,
}: {
  kind: PeriodKind;
  onKind(k: PeriodKind): void;
  custom: { from: string; to: string };
  onCustom(r: { from: string; to: string }): void;
  range: { from: string; to: string };
  kinds: readonly PeriodKind[];
}) {
  const { t } = useTranslation();
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ md: 'center' }}
      sx={{ mb: 2 }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={kind}
        onChange={(_, v: PeriodKind | null) => v && onKind(v)}
      >
        {kinds.map((k) => (
          <ToggleButton key={k} value={k}>
            {t(`billing.period_${k}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {kind === 'custom' ? (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="date"
            label={t('billing.from')}
            value={custom.from}
            onChange={(e) => onCustom({ ...custom, from: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label={t('billing.to')}
            value={custom.to}
            onChange={(e) => onCustom({ ...custom, to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      ) : (
        <Typography color="text.secondary" data-testid="period">
          {range.from} – {range.to}
        </Typography>
      )}
    </Stack>
  );
}

/** A warehouse's (or a location's) Transfers tab: its own period and the one list, no billing wording. */
export function PlaceMovements({ siteIds }: { siteIds: string[] }) {
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const today = isoDateIn(tz);
  const [kind, setKind] = useState<PeriodKind>('this_month');
  const [custom, setCustom] = useState(() => periodRange('this_month', today));
  const range =
    kind === 'custom' ? custom : periodRange(kind as Exclude<ShortPeriod, 'custom'>, today);
  const bounds = periodInstants(range, tz);
  const movement = useMovementViews(siteIds, bounds?.fromIso ?? '', bounds?.toIso ?? '');
  return (
    <Box data-testid="place-movements">
      <PeriodPicker
        kind={kind}
        onKind={setKind}
        custom={custom}
        onCustom={setCustom}
        range={range}
        kinds={['this_month', 'last_month', 'custom']}
      />
      <MovementsTable {...movement} showBilling={false} />
    </Box>
  );
}
